import type { TFunction } from "i18next";
import { subjectLine } from "@/shared/lib/subject";
import type { Alert, AlertEpisode } from "@/shared/api/models";
import { gasSymbol } from "@/widgets/control/useControlData";

/**
 * 경보 한 건을 **사람 말로** — 목록·상세·관제 피드가 같은 문장을 쓴다 (2026-09-24 지시).
 *
 * 지적: 「경보내역이 제대로 안 보인다 — 글자가 다 잘리고 숨겨진다」. 설비 무응답 경보가
 * 「복합 가스 · 심각 RI 0.00」으로 찍혔고(가스가 빈 문자열이라 복합 가스로 떨어졌다),
 * 본문은 「Node sensor2-01 RI=0.38 · 자동 해제 …」 같은 내부 표기였다.
 */

const GAS_CODES = ["CO", "H2S", "NH3", "CH4", "CO2", "O2", "SO2", "NO2", "VOC"];

/** 「산소」·「황화수소」 — 모르는 코드는 그대로. */
export function gasName(t: TFunction, code: string | null | undefined): string {
  const c = (code ?? "").toUpperCase();
  if (!c) return t("widgets.alerts.v2.compositeRisk");
  return GAS_CODES.includes(c) ? t(`widgets.emergency.gas.${c}`) : c;
}

/** 설비 무응답(통신 이상)인가 — `kind` 가 없던 옛 응답은 subject_key 로 가른다. */
export function isComm(a: Pick<Alert, "kind" | "subject_key" | "gas">): boolean {
  if (a.kind) return a.kind === "health";
  return !a.gas && !!a.subject_key && a.subject_key.includes(":");
}

/**
 * 설비 경보 대상. ``lamp`` = 현장 PC 경광등 끊김(서버 ``lamp:{매니져}``, 2026-11-04 — 매니져 3.0.216~ 보고).
 * 경광등은 「응답 없음」이 아니라 「안 붙어 있음」이다 — 매니져는 살아서 판정·방송한다.
 */
export type CommTarget = "manager" | "node" | "site" | "lamp" | "other";

export function commTarget(subjectKey: string | null | undefined): CommTarget {
  const k = (subjectKey ?? "").split(":", 1)[0];
  return k === "manager" || k === "node" || k === "site" || k === "lamp" ? k : "other";
}

/** 목록·상세 제목. 가스 = 「산소 경보」, 통신 = 「현장 PC 응답 없음」. 등급은 옆 칩이 말한다. */
export function alertTitle(t: TFunction, a: Alert): string {
  if (isComm(a)) return t(`widgets.alerts.v2.comm.${commTarget(a.subject_key)}`);
  return t("widgets.alerts.v2.gasAlert", { gas: gasName(t, a.gas) });
}

/** 종류 칩 — 「가스 경보」 / 「통신 이상」 / 「훈련」. */
export function kindLabel(t: TFunction, a: Alert): string {
  if (isComm(a) && commTarget(a.subject_key) === "lamp") return t("widgets.alerts.v2.kind.equip");
  return isComm(a) ? t("widgets.alerts.v2.kind.comm") : t("widgets.alerts.v2.kind.gas");
}

/** 어디서 — 「테스트_금산2 · sensor2-01(1층 전기실)」. 통신 이상은 대상 장비 이름을 붙인다. */
export function whereLine(t: TFunction, a: Alert): string {
  const base = subjectLine(a);
  if (isComm(a) && commTarget(a.subject_key) === "manager") {
    const pc = managerName(a.message ?? "");
    return [a.site_name, pc || t("widgets.alerts.v2.comm.managerShort")].filter(Boolean).join(" · ");
  }
  if (isComm(a) && commTarget(a.subject_key) === "lamp") {
    const pc = lampManagerName(a.message ?? "");
    return [a.site_name, pc || t("widgets.alerts.v2.comm.managerShort")].filter(Boolean).join(" · ");
  }
  return base;
}

/** 「매니져 현장2 경광등 끊김 12분 — …」 → 「현장2」(서버 ops scan_anomalies 문구). */
function lampManagerName(message: string): string {
  const m = /^매니져\s+(.{1,40}?)\s+경광등 끊김/.exec(message);
  return m?.[1]?.trim() ?? "";
}

/** 「매니져 [현장] 테스트_금산2 매니져 (PC33) 11분 무응답 — …」 → 「PC33」 */
function managerName(message: string): string {
  const m = /\(([^()]{1,24})\)\s*\d+\s*분\s*무응답/.exec(message);
  return m?.[1]?.trim() ?? "";
}

/** 무응답 길이(분) — 「11분 무응답」「3분 무수신」. */
export function outageMinutes(message: string | null | undefined): number | null {
  const m = /(\d+)\s*분\s*무(?:응답|수신)/.exec(message ?? "");
  return m ? Number(m[1]) : null;
}

/**
 * 본문을 사람 말로. 내부 표기(「Node x RI=0.38」)는 떼고 뒤에 붙은 설명만 남긴다 —
 * 위험도 숫자는 막대가 따로 보여 준다.
 */
export function plainMessage(t: TFunction, a: Alert): string {
  const msg = (a.message ?? "").trim();
  if (isComm(a)) {
    const min = outageMinutes(msg);
    const target = commTarget(a.subject_key);
    if (min != null) return t(`widgets.alerts.v2.commMsg.${target}`, { min });
    if (/수신 이력 없음/.test(msg)) return t("widgets.alerts.v2.commMsg.never");
    return msg;
  }
  const rest = msg.replace(/^Node\s+\S+\s+RI=\d+(?:\.\d+)?\s*(?:·\s*)?/i, "").trim();
  return rest;
}

/** 「2시간 21분」 / 「45분」 / 「30초」 */
export function formatDuration(t: TFunction, seconds: number | null | undefined): string {
  if (seconds == null || !Number.isFinite(seconds)) return "—";
  const s = Math.max(0, Math.round(seconds));
  if (s < 60) return t("widgets.alerts.v2.dur.sec", { n: s });
  const m = Math.round(s / 60);
  if (m < 60) return t("widgets.alerts.v2.dur.min", { n: m });
  const h = Math.floor(m / 60);
  const mm = m % 60;
  if (h < 48) return mm ? t("widgets.alerts.v2.dur.hmin", { h, m: mm }) : t("widgets.alerts.v2.dur.hour", { n: h });
  return t("widgets.alerts.v2.dur.day", { n: Math.round(h / 24) });
}

/** 「09-24 00:31」 — 24시간 표기(좁은 폭에서 「오후 12:31」은 두 배를 먹는다). */
export function shortTime(iso: string | null | undefined, withDate = true): string {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "—";
  const p2 = (n: number) => String(n).padStart(2, "0");
  const hm = `${p2(d.getHours())}:${p2(d.getMinutes())}`;
  return withDate ? `${p2(d.getMonth() + 1)}-${p2(d.getDate())} ${hm}` : hm;
}

/** 같은 날이면 시각만 — 「00:20 → 05:47」, 날이 바뀌면 날짜까지. */
export function spanText(startIso: string | null | undefined, endIso: string | null | undefined): string {
  const a = startIso ? new Date(startIso) : null;
  const b = endIso ? new Date(endIso) : null;
  const sameDay = a && b && a.toDateString() === b.toDateString();
  return `${shortTime(startIso)} → ${endIso ? shortTime(endIso, !sameDay) : "…"}`;
}

/** 통신 이상 사건 제목 — 「현장 PC 응답 없음」. */
export function episodeTitle(t: TFunction, e: AlertEpisode): string {
  return t(`widgets.alerts.v2.comm.${commTarget(e.subject_key)}`);
}

/** 경보가 얼마나 이어졌나 — 해제됐으면 발령→해제, 아니면 지금까지. */
export function alertDurationS(a: Alert, now = Date.now()): number | null {
  if (!a.raised_at) return null;
  const start = Date.parse(a.raised_at);
  const end = a.cleared_at ? Date.parse(a.cleared_at) : now;
  if (Number.isNaN(start) || Number.isNaN(end)) return null;
  return Math.max(0, (end - start) / 1000);
}

/** 등급별 [확인] 목표(초) — 심각 1분 · 경계 3분 · 주의 10분(2026-10-29 사용자 결정, 10-12 검증 권장값).
 *  설비 무응답·등급 0 은 목표가 없다. */
export const ACK_TARGET_S: Readonly<Record<1 | 2 | 3, number>> = { 3: 60, 2: 180, 1: 600 };

/** 확인 목표 대비 — 아직 안 눌렀으면 지금까지, 눌렀으면 누르기까지 걸린 시간. 목표가 없는 경보는 null. */
export function ackTargetInfo(
  a: Alert, now = Date.now(),
): { targetS: number; elapsedS: number; over: boolean; acked: boolean } | null {
  const lv = a.level ?? 0;
  if (isComm(a) || (lv !== 1 && lv !== 2 && lv !== 3) || !a.raised_at) return null;
  const start = Date.parse(a.raised_at);
  if (Number.isNaN(start)) return null;
  const ackAt = a.acknowledged_at ? Date.parse(a.acknowledged_at) : NaN;
  const acked = !Number.isNaN(ackAt);
  // 확인 없이 해제된 경보는 잴 끝이 없다 — 목표 표시를 하지 않는다.
  if (!acked && a.state !== "open") return null;
  const targetS = ACK_TARGET_S[lv];
  const elapsedS = Math.max(0, ((acked ? ackAt : now) - start) / 1000);
  return { targetS, elapsedS, over: elapsedS > targetS, acked };
}

/** 해제가 사람 손인지 자동(측정값 복귀·장비 복구)인지 — 원장·본문으로 가른다. */
export function clearedHow(a: Alert): "auto" | "person" | null {
  if (a.state !== "cleared") return null;
  const d = (a.delivery ?? {}) as Record<string, { detail?: string } | undefined>;
  if (d.recovery) return "auto";
  if (d.cleared_by) return "person";
  // 현장 해제(매니져가 풀었다)·현장 확인 끊김(클라우드가 이어 받음)·앞 사건 행 닫음도 사람 손이 아니다(2026-10-13).
  if (/자동 해제|설비 응답 복구|현장 해제|현장 확인 끊김/.test(a.message ?? "")) return "auto";
  return null;
}

export function clearedByName(a: Alert): string {
  const d = (a.delivery ?? {}) as Record<string, { detail?: string } | undefined>;
  return d.cleared_by?.detail ?? "";
}

/** 해제 사유(서버 원장 ``clear_reason``) — 받기만 하고 어디에도 안 보였다(10-12 검증 V-31, 10-29 재점검). */
export function clearReason(a: Alert): string {
  const d = (a.delivery ?? {}) as Record<string, { detail?: string } | undefined>;
  return d.clear_reason?.detail ?? "";
}

/**
 * 발령 당시 **위험도(RI)** — 위험 엔진이 낸 경보만 값이 RI 다. 현장 PC 가 올린 경보
 * (`/api/sensors/alert/`)는 `ri_at_open` 자리에 **가스 측정값**이 들어 있어, 막대에 그리면
 * 1.00 으로 가득 찬 거짓 막대가 된다(2026-09-24 리뷰). 엔진 경보의 본문은 「Node x RI=0.38」로 시작한다.
 */
export function riAtRaise(a: Alert): number | null {
  if (isComm(a)) return null;
  if (!/^Node\s+\S+\s+RI=/i.test(a.message ?? "")) return null;
  const v = a.ri_at_open;
  return v == null || Number.isNaN(v) ? null : v;
}

/** 통신 이상 사건 상태 → 칩 문구 키. */
export function episodeStatusKey(e: AlertEpisode): string {
  if (isEpisodeOngoing(e)) return "widgets.alerts.v2.sum.stillDown";
  if (e.status === "cleared_unconfirmed") return "widgets.alerts.v2.sum.clearedUnconfirmed";
  return "widgets.alerts.v2.sum.recovered";
}

/** 통신 이상 사건이 아직 이어지는가 — `status` 가 없던 옛 서버(3.0.24 이하)는 `ongoing` 만 본다. */
export function isEpisodeOngoing(e: AlertEpisode): boolean {
  return e.ongoing || e.status === "ongoing";
}

/* ─────────── 관제 「경보·통신 요약」용 (2026-09-29 관제 개편 P1) ─────────── */

/**
 * 한 줄 제목 — 가스 = 「CH₄ 메탄(가연성)」(기호 + 이름), 통신 = 「현장 PC 응답 없음」.
 * 등급은 옆 칩이 말한다. `gasSymbol` 은 관제 보드 공용 기호표(아래첨자)라 타일·요약이 같은 표기를 쓴다.
 * 이름 사전에 없는 채널(SMOKE 등)은 기호표가 「연기」로 풀어 주므로 그것 하나만 쓴다.
 */
export function alertHeadline(t: TFunction, a: Alert, lang: string | undefined): string {
  if (isComm(a)) return alertTitle(t, a);
  const code = (a.gas ?? "").toUpperCase();
  const name = gasName(t, code);           // 빈 코드 = 「복합 가스」, 모르는 코드 = 코드 그대로
  if (!code) return name;
  const sym = gasSymbol(code, lang);
  if (name === code) return sym || name;
  return sym === name ? name : `${sym} ${name}`;
}

/**
 * 어디서 — 현장 이름을 뺀 짧은 표기. 요약은 이미 한 현장 범위라 현장명을 또 쓰면
 * 350px 열에서 함체 이름이 잘린다. 가스 = 「sensor2-01(1층 전기실)」, 현장 PC 무응답 = 「PC33」.
 */
export function alertWhereShort(t: TFunction, a: Alert): string {
  if (isComm(a) && commTarget(a.subject_key) === "manager") {
    return managerName(a.message ?? "") || t("widgets.alerts.v2.comm.managerShort");
  }
  if (isComm(a) && commTarget(a.subject_key) === "lamp") {
    return lampManagerName(a.message ?? "") || t("widgets.alerts.v2.comm.managerShort");
  }
  return subjectLine({ node_code: a.node_code ?? null, node_label: a.node_label ?? null });
}

/** 경보 상태 → 짧은 칩 문구 키 — 진행 중 / 확인됨 / 해제. */
export function alertStateKey(a: Pick<Alert, "state">): string {
  if (a.state === "cleared") return "pages.alerts.filter.cleared";
  if (a.state === "acknowledged") return "pages.alerts.filter.acknowledged";
  return "widgets.alerts.v2.sum.ongoing";
}

/** `"manager:7"` → 7. 현장 PC 가 아니거나 숫자가 아니면 null. */
export function managerIdOf(subjectKey: string | null | undefined): number | null {
  if (commTarget(subjectKey) !== "manager") return null;
  const n = Number((subjectKey ?? "").split(":")[1]);
  return Number.isInteger(n) && n > 0 ? n : null;
}

/** 「마지막 경보」가 되는 창 — 이보다 오래된 해제 경보는 「최근 24시간 경보 없음」으로 둔다. */
export const LAST_ALERT_WINDOW_MS = 24 * 3600_000;

/**
 * 가장 최근 경보 1건 — 가스·통신 모두. 훈련 경보는 `withDrill`(그 현장이 지금 훈련 중)일 때만 넣는다.
 * 해제된 경보는 24시간 안에 발령된 것만, **아직 안 끝난 경보는 오래돼도** 후보다(끝나지 않은 일이
 * 「경보 없음」 뒤로 숨지 않게). 정렬은 발령 시각 — 목록이 어떤 순서로 와도 같은 답을 낸다.
 */
export function lastAlertOf(
  alerts: readonly Alert[],
  opts: { now: number; withDrill: boolean; siteId?: number | null | undefined },
): Alert | null {
  let best: Alert | null = null;
  let bestAt = -Infinity;
  for (const a of alerts) {
    if (a.id == null) continue;
    if (opts.siteId != null && a.site !== opts.siteId) continue;
    if (a.is_drill && !opts.withDrill) continue;
    const at = Date.parse(a.raised_at ?? "");
    if (!Number.isFinite(at)) continue;
    if (a.state === "cleared" && opts.now - at > LAST_ALERT_WINDOW_MS) continue;
    if (at > bestAt) {
      best = a;
      bestAt = at;
    }
  }
  return best;
}
